import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";

// Pages
import Login from "./pages/Auth/Login";
import Signup from "./pages/Auth/Signup";
import QRSignup from "./pages/QRSignup/QRSignup";
import SignupRedirect from "./pages/SignupRedirect/SignupRedirect";
import LoyaltyDirectory from "./pages/LoyaltyDirectory/LoyaltyDirectory";
import VenueDetail from "./pages/VenueDetail/VenueDetail";
import Wishlist from "./pages/Wishlist/Wishlist";
import Profile from "./pages/Profile/Profile";
import EventDetail from "./pages/EventDetail/EventDetail";
import StudioShowdownVotingPage from "./pages/StudioShowdown/StudioShowdownVotingPage";
import SilentAuctionPage from "./pages/SilentAuction/SilentAuctionPage";
import EventTicketPage from "./pages/Ticketing/EventTicketPage";
import ReservationConfirmation from "./pages/Ticketing/ReservationConfirmation";
import MyTickets from "./pages/Ticketing/MyTickets";
import TicketDetail from "./pages/Ticketing/TicketDetail";
import VenueInterest from "./pages/VenueInterest/VenueInterest";
import CompanySelection from "./pages/CompanySelection/CompanySelection";
import FeedbackSurvey from "./pages/FeedbackSurvey/FeedbackSurvey";

// Legal Pages
import PrivacyPolicy from "./pages/Legal/PrivacyPolicy";
import TermsOfService from "./pages/Legal/TermsOfService";
import CookiePolicy from "./pages/Legal/CookiePolicy";

// Components
import Layout from "./components/Layout/Layout";
import LoadingPage from "./components/Loading/LoadingPage";

// Hooks
import usePageTracking from "./hooks/usePageTracking";

function AppRoutes({ token, setToken }) {
  const { showLoading } = useAuth();

  // Track page views for Google Analytics
  usePageTracking();

  return (
    <>
      {/* Show full-page loading overlay when AuthContext's showLoading is true */}
      {showLoading && <LoadingPage />}

      <Routes>
        {/* Loading Page */}
        <Route path="/loading" element={<LoadingPage />} />

        {/* Authentication Routes */}
        <Route path="/staff-login" element={<Login setToken={setToken} />} />
        <Route path="/login" element={<Login setToken={setToken} />} />
        <Route path="/signup/:code" element={<SignupRedirect />} />
        <Route path="/signup" element={<Signup setToken={setToken} />} />
        <Route path="/perks-signup" element={<QRSignup />} />
        <Route path="/loyalty-signup" element={<QRSignup />} /> {/* Legacy */}

        {/* Perks Directory Routes */}
        <Route path="/perks-directory/:code" element={<Layout><LoyaltyDirectory /></Layout>} />
        <Route path="/perks-directory" element={<Layout><LoyaltyDirectory /></Layout>} />
        <Route path="/loyalty-directory/:code" element={<Layout><LoyaltyDirectory /></Layout>} /> {/* Legacy */}
        <Route path="/loyalty-directory" element={<Layout><LoyaltyDirectory /></Layout>} /> {/* Legacy */}

        {/* Venue Routes */}
        <Route path="/become-partner" element={<VenueInterest />} />
        <Route path="/company-selection" element={<CompanySelection />} />
        <Route path="/coupons/:customerToken" element={<Layout><VenueDetail /></Layout>} />
        <Route path="/coupons/:customerToken/venue/:venueId" element={<Layout><VenueDetail /></Layout>} />

        {/* User Routes */}
        <Route path="/wishlist/:customerToken" element={<Layout><Wishlist /></Layout>} />
        <Route path="/profile" element={<Layout><Profile /></Layout>} />

        {/* Event Routes */}
        <Route path="/events/:eventSlug" element={<Layout><EventDetail /></Layout>} />
        <Route path="/events/:eventSlug/vote" element={<Layout><StudioShowdownVotingPage /></Layout>} />
        <Route path="/events/:eventSlug/auction" element={<Layout><SilentAuctionPage /></Layout>} />
        <Route path="/events/:eventSlug/tickets" element={<Layout><EventTicketPage /></Layout>} />

        {/* Ticketing Routes */}
        <Route path="/tickets/confirmation" element={<Layout><ReservationConfirmation /></Layout>} />
        <Route path="/tickets/:ticketCode" element={<Layout><TicketDetail /></Layout>} />
        <Route path="/my-tickets" element={<Layout><MyTickets /></Layout>} />

        {/* Event Feedback Survey (public, no Layout wrapper — standalone page) */}
        <Route path="/feedback/:token" element={<FeedbackSurvey />} />

        {/* Legal Pages */}
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/cookies" element={<CookiePolicy />} />

        {/* Default Routes */}
        <Route path="/" element={<Layout><LoyaltyDirectory /></Layout>} />
        <Route path="/:code" element={<Layout><LoyaltyDirectory /></Layout>} />
      </Routes>
    </>
  );
}

function App() {
  const getToken = () => {
    const tokenString = sessionStorage.getItem("token");
    return tokenString;
  };

  const [token, setToken] = useState(getToken());

  // Add global error handler for extension context errors
  useEffect(() => {
    const handleGlobalError = (event) => {
      // Suppress extension context errors that don't affect functionality
      if (event.error && event.error.message && event.error.message.includes("Extension context invalidated")) {
        event.preventDefault();
        console.warn("Extension context error suppressed:", event.error.message);
        return false;
      }
    };

    const handleUnhandledRejection = (event) => {
      // Suppress extension context promise rejections
      if (event.reason && event.reason.message && event.reason.message.includes("Extension context invalidated")) {
        event.preventDefault();
        console.warn("Extension context promise rejection suppressed:", event.reason.message);
        return false;
      }
    };

    window.addEventListener("error", handleGlobalError);
    window.addEventListener("unhandledrejection", handleUnhandledRejection);

    return () => {
      window.removeEventListener("error", handleGlobalError);
      window.removeEventListener("unhandledrejection", handleUnhandledRejection);
    };
  }, []);

  useEffect(() => {
    if (token) {
      sessionStorage.setItem("token", token);
    } else {
      sessionStorage.removeItem("token");
    }
  }, [token]);

  return (
    <AuthProvider>
      <Router>
        <div>
          <AppRoutes token={token} setToken={setToken} />
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;

/* =========================
   Archived / Commented Code
   ========================= */

// --- Deprecated Imports ---
// Date: 2026-01-08
// Reason: These features were removed or moved to archive/

// import AuthenticatedApp from "./AuthenticatedApp";
// import PasswordReset from "./components/PasswordReset/PasswordReset";
// import QRCodeRedirectHandler from "./components/QRCodeRedirectHandler/QrCodeRedirectHandler";
// import ViewInventory from "./components/ViewInventory/ViewInventory";
// import EditInventory from "./components/EditInventory/EditInventory";
// import SubscriptionPage from "./pages/Subscription/SubscriptionPage";
// import SelectPOSPage from "./pages/SelectPOSPage/SelectPOSPage";
// import ImplementationFeePage from "./pages/ImplementationFeePage/ImplementationFeePage";
// import UploadInvoiceSamplePage from "./pages/UploadInvoiceSamplePage/UploadInvoiceSamplePage";
// import CancelFeedback from "./components/Feedback/CancelFeedback";
// import ServiceSelection from "./pages/ServiceSelection/ServiceSelection";
// import LoyaltyQuestionnaire from "./pages/LoyaltyQuestionnaire/LoyaltyQuestionnaire.jsx";
// import ContactUs from "./pages/ContactUs/ContactUs";

// --- Deprecated Routes ---
// Date: 2026-01-08
// Reason: These routes are no longer active

// Password Reset Route (moved to separate flow):
// <Route path="/reset-password" element={<PasswordReset />} />

// Inventory Routes (feature deprecated):
// <Route path="/qrcode/:inventoryId" element={<QRCodeRedirectHandler token={token} />} />
// <Route path="/view-inventory/:inventoryId" element={<ViewInventory />} />

// Loyalty Questionnaire Routes (feature removed):
// <Route path="/perks-questionnaire" element={<Layout><LoyaltyQuestionnaire /></Layout>} />
// <Route path="/loyalty-questionnaire" element={<Layout><LoyaltyQuestionnaire /></Layout>} />

// Contact Us Route (moved to footer links):
// <Route path="/contact-us" element={<ContactUs />} />

// POS & Implementation Routes (feature deprecated):
// <Route path="/select-pos" element={<SelectPOSPage />} />
// <Route path="/implementation-fee" element={<ImplementationFeePage />} />
// <Route path="/upload-invoice-sample" element={<UploadInvoiceSamplePage />} />

// Authenticated App Pattern (simplified to current auth context):
// {token ? (
//   <Route path="/*" element={<AuthenticatedApp setToken={setToken} />} />
// ) : (
//   ...
// )}
